import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  HistoryDateFilterItem,
  HistoryPageFilter,
  OperationHistoryFilterItem,
} from '@/shared/types';

type Props = {
  operationTypes: OperationHistoryFilterItem[];
  dateFilters: HistoryDateFilterItem[];
};

export const useHistoryPageFilters = ({
  operationTypes,
  dateFilters,
}: Props) => {
  const [searchParams] = useSearchParams();
  const initialFilter = operationTypes[0]?.filter ?? 'all';
  const requestedDateFilter = searchParams.get('datefilter');
  const isRequestedDateFilterApplied = useRef(false);

  const [filters, setFilters] = useState<HistoryPageFilter>({
    filter: initialFilter,
    datefilter: undefined,
    datefrom: undefined,
    dateto: undefined,
  });

  useEffect(() => {
    if (isRequestedDateFilterApplied.current || !requestedDateFilter) return;

    const option = dateFilters.find(
      item => item.datefilter === requestedDateFilter
    );
    if (!option) return;

    isRequestedDateFilterApplied.current = true;
    setFilters(prev => ({ ...prev, datefilter: option.datefilter }));
  }, [requestedDateFilter, dateFilters]);

  const resetPeriod = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      datefilter: undefined,
      datefrom: undefined,
      dateto: undefined,
    }));
  }, [setFilters]);

  const isPeriodApplied = useMemo(() => {
    return (
      Boolean(filters.datefilter) ||
      (Boolean(filters.datefrom) && Boolean(filters.dateto))
    );
  }, [filters]);

  return {
    filters,
    setPageFilters: setFilters,
    resetPeriod,
    isPeriodApplied,
  };
};
